/**
 * 바닥글
 * ---------------------------------------------------------------------------
 * 홍보 사이트(lumina)의 푸터와 같은 내용을 담는다. 같은 회사의 서비스인데
 * 사이트마다 사업자 정보가 다르게 보이면 신뢰를 잃는다.
 *
 * 왜 전자상거래 표시가 필요한가
 *   유료 구독을 받는 화면이 있는 서비스는 통신판매업자 표시 의무가 있다.
 *   상호·대표자·주소·전화·사업자등록번호·통신판매업 신고번호를 이용자가 쉽게
 *   볼 수 있는 곳에 둔다.
 *
 * 값은 서버에서 읽는다
 *   예전에는 빌드 시점 환경변수(VITE_FOOTER_*)였다. Vite 가 번들에 박아
 *   넣으므로 **전화번호 한 자리를 고치는 데도 다시 빌드해서 배포**해야 했다.
 *   이제 시스템 관리자가 「시스템 › 바닥글」 화면에서 고치면 곧바로 바뀐다.
 *
 *   비어 있는 항목은 줄째로 빼므로, 값이 없다고 "팩스: " 같은 빈 칸이 남지
 *   않는다. 전부 공개 정보이므로 로그인 전에도 읽힌다.
 */

import { useQuery } from '@tanstack/react-query';
import { Layout, Space, Typography } from 'antd';
import { apiClient } from '../lib/api-client';

const { Text, Link } = Typography;

/**
 * 약관·방침은 **작은 창**으로 띄운다.
 *
 * 새 탭으로 열면 읽고 나서 원래 탭을 다시 찾아야 한다. 모바일에서는 탭
 * 목록을 뒤져야 하고, 그 사이 보던 화면이 어디였는지 잊는다. 작은 창은
 * 닫는 순간 보던 화면으로 돌아온다. 홍보 사이트도 같은 방식이다.
 *
 * 팝업이 막히면 조용히 넘어가지 않고 새 탭으로라도 연다 — 약관을 못 보는
 * 것보다 낫다. 어느 쪽이든 문서는 보인다.
 */
function openPolicyWindow(url: string, name: string) {
  const opened = window.open(url, name, 'width=860,height=900,scrollbars=yes,resizable=yes');
  if (!opened || opened.closed) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  opened.focus();
}

export interface SiteFooterData {
  companyName: string | null;
  ceoName: string | null;
  address: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  bizRegNo: string | null;
  mailOrderNo: string | null;
  privacyUrl: string | null;
  termsUrl: string | null;
  copyright: string | null;
}

/** 값이 비면 그 항목은 통째로 뺀다. */
function present(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return v === '' ? null : v;
}

export function AppFooter() {
  /*
   * 로그인 전에도 그려지는 자리다. 실패해도 화면을 막지 않는다 — 바닥글이
   * 안 보이는 것보다 로그인이 막히는 쪽이 훨씬 나쁘다.
   */
  const { data } = useQuery<SiteFooterData>({
    queryKey: ['public', 'footer'],
    queryFn: async () => (await apiClient.get<SiteFooterData>('/public/footer')).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const company = present(data?.companyName);
  const address = present(data?.address);
  const copyright = present(data?.copyright);
  const privacyUrl = present(data?.privacyUrl);
  const termsUrl = present(data?.termsUrl);

  const contacts = [
    { label: '대표이사', value: present(data?.ceoName) },
    { label: '대표전화', value: present(data?.phone) },
    { label: '팩스', value: present(data?.fax) },
    { label: '이메일', value: present(data?.email) },
  ].filter((x): x is { label: string; value: string } => x.value !== null);

  const business = [
    { label: '사업자등록번호', value: present(data?.bizRegNo) },
    { label: '통신판매업 신고번호', value: present(data?.mailOrderNo) },
  ].filter((x): x is { label: string; value: string } => x.value !== null);

  // 넣을 것이 하나도 없으면 빈 띠만 남는다. 그럴 바엔 그리지 않는다.
  const empty =
    !company && !address && !copyright && contacts.length === 0 && business.length === 0;
  if (empty) return null;

  return (
    <Layout.Footer
      style={{
        background: '#0f172a',
        color: 'rgba(255,255,255,0.65)',
        fontSize: 12,
        lineHeight: 1.9,
        padding: '20px 24px',
      }}
    >
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        {(company || privacyUrl || termsUrl) && (
          <Space wrap size={12} style={{ justifyContent: 'space-between', width: '100%' }}>
            {company && (
              <Text strong style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>
                {company}
              </Text>
            )}
            <Space wrap size={12}>
              {/*
                href 를 남겨 둔다 — 오른쪽 클릭으로 주소를 복사하거나 새 탭에서
                열고 싶은 사람이 있고, 스크린 리더도 링크로 읽는다.
                왼쪽 클릭만 가로채 작은 창으로 띄운다.
              */}
              {privacyUrl && (
                <Link
                  href={privacyUrl}
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openPolicyWindow(privacyUrl, 'worknhub-policy-privacy');
                  }}
                >
                  개인정보처리방침
                </Link>
              )}
              {termsUrl && (
                <Link
                  href={termsUrl}
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openPolicyWindow(termsUrl, 'worknhub-policy-terms');
                  }}
                >
                  이용약관
                </Link>
              )}
            </Space>
          </Space>
        )}

        {address && <div>{address}</div>}

        {contacts.length > 0 && <div>{contacts.map(join).join('  |  ')}</div>}
        {business.length > 0 && <div>{business.map(join).join('  |  ')}</div>}

        {copyright && (
          <Text style={{ color: 'rgba(255,255,255,0.45)' }}>{copyright}</Text>
        )}
      </Space>
    </Layout.Footer>
  );
}

const join = (x: { label: string; value: string }) => `${x.label}: ${x.value}`;
